import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { Drawer, Empty, Input, Spin, Tag } from 'antd';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useHistory, useLocation, useParams, useRouteMatch } from 'react-router-dom';

import BoxContainer from '@src/components/BoxContainer';
import Title from '@src/components/BoxContainer/components/Title';
import ActionButton from '@src/components/Buttons/ActionButton';
import FilterContainer from '@src/components/FilterContainer';
import MemberAvatar from '@src/components/MemberList/MemberAvatar';
import { ResponsiveTable } from '@src/components/Table';
import type { ResponsiveTableColumns } from '@src/components/Table';
import type { IWorkJourneyManagementUser } from '@src/models/interfaces/workJourneyManagement.interface';
import { getFullUrl } from '@src/packages/utils';
import { ALLOWED_ROLES } from '@src/packages/utils/access';
import {
  formatAttendanceIdentifierDisplay,
  getOrganizationCountry,
  type OrganizationCountrySource,
} from '@src/packages/utils/nationalId';
import { getRouteToCoverAbsenceReplaceDrawer, PATHS } from '@src/packages/workJourney/constants';
import IntlMessages from '@src/util/IntlMessages';

import { formatCoverDrawerTitle } from '../formatCoverDrawerTitle';
import { formatCoverUserDisplayName } from '../formatCoverUserDisplayName';
import { loadOrganizationUsersByIds } from '../loadOrganizationUsersByIds';
import {
  mergeCoverUsersWithOrgProfiles,
  needsCoverUserProfileHydration,
} from '../mergeCoverUsersWithOrgProfiles';
import type { IWorkJourneyCoverDrawerLocationState, IWorkJourneyCoverDrawerParams } from '../types';
import { WorkJourneyCoverConflictAlert } from '../WorkJourneyCoverConflictAlert';
import { getCoverPickerRowKey } from '../coverPickerRowKey';
import {
  matchesCoverUserSearch,
  normalizeCoverUserSearchQuery,
} from './matchesCoverUserSearch';
import styles from './styles.module.less';

export interface WorkJourneyCoverAbsenceDrawerProps {
  open?: boolean;
  onClose?: () => void;
  onReplace?: (user: IWorkJourneyManagementUser) => void;
  title?: string;
  actionLabel?: string;
  sectionTitle?: ReactNode;
  width?: number | string;
  journeyName?: string;
  currentMembers?: number;
  requiredMembers?: number;
  absentUsers?: IWorkJourneyManagementUser[];
  showConflict?: boolean;
  renderStatus?: (user: IWorkJourneyManagementUser) => ReactNode;
  searchPlaceholderId?: string;
}

export const WorkJourneyCoverAbsenceDrawer = ({
  open,
  onClose,
  onReplace,
  title: titleProp,
  actionLabel,
  sectionTitle,
  width = '100%',
  journeyName: journeyNameProp,
  currentMembers: currentMembersProp,
  requiredMembers: requiredMembersProp,
  absentUsers: absentUsersProp,
  showConflict = true,
  renderStatus,
  searchPlaceholderId = 'workJourney.management.cover.absence.searchPlaceholder',
}: WorkJourneyCoverAbsenceDrawerProps) => {
  const intl = useIntl();
  const history = useHistory();
  const routeMatch = useRouteMatch();
  const url = routeMatch?.url ?? '';
  const { workJourneyConfigId, date } = useParams<IWorkJourneyCoverDrawerParams>();
  const location = useLocation<IWorkJourneyCoverDrawerLocationState | undefined>();
  const {
    shadow,
    journeyName: locationJourneyName,
    currentMembers: locationCurrentMembers,
    requiredMembers: locationRequiredMembers,
    absentUsers: locationAbsentUsers = [],
  } = location.state ?? {};
  const journeyName = journeyNameProp ?? locationJourneyName;
  const currentMembers = currentMembersProp ?? locationCurrentMembers;
  const requiredMembers = requiredMembersProp ?? locationRequiredMembers;
  const absentUsersFromPropsOrState = absentUsersProp ?? locationAbsentUsers;
  const organizationId = useSelector(
    ({ organizations }: { organizations: { organization?: { id?: string } } }) =>
      organizations.organization?.id,
  );
  const organizationCountry = useSelector(
    ({ organizations }: { organizations: { organization?: OrganizationCountrySource } }) =>
      getOrganizationCountry(organizations.organization),
  );

  const [absentUsers, setAbsentUsers] = useState(absentUsersFromPropsOrState);
  const [isHydratingProfiles, setIsHydratingProfiles] = useState(false);
  const [search, setSearch] = useState('');
  const isOpen = open ?? Boolean(workJourneyConfigId && date);

  useEffect(() => {
    setAbsentUsers(absentUsersFromPropsOrState);
  }, [absentUsersFromPropsOrState]);

  useEffect(() => {
    if (!isOpen) {
      setSearch('');
    }
  }, [isOpen]);

  useEffect(() => {
    const idsNeedingHydration = absentUsersFromPropsOrState
      .filter(needsCoverUserProfileHydration)
      .map(user => user.id)
      .filter(Boolean);
    if (!organizationId || idsNeedingHydration.length === 0) {
      setIsHydratingProfiles(false);
      return undefined;
    }

    let cancelled = false;
    setIsHydratingProfiles(true);
    void loadOrganizationUsersByIds(organizationId, idsNeedingHydration)
      .then(profilesById => {
        if (cancelled) return;
        if (profilesById.size > 0) {
          setAbsentUsers(current => mergeCoverUsersWithOrgProfiles(current, profilesById));
        }
        setIsHydratingProfiles(false);
      })
      .catch(() => {
        if (!cancelled) {
          setIsHydratingProfiles(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [absentUsersFromPropsOrState, organizationId]);

  const title = useMemo(
    () => titleProp ?? formatCoverDrawerTitle(journeyName, date, intl),
    [date, intl, journeyName, titleProp],
  );

  const filteredUsers = useMemo(() => {
    const query = normalizeCoverUserSearchQuery(search);
    if (!query) return absentUsers;
    return absentUsers.filter(user =>
      matchesCoverUserSearch(user, query, organizationCountry),
    );
  }, [absentUsers, organizationCountry, search]);

  const tableRows = useMemo(
    () =>
      filteredUsers.map(user => ({
        ...user,
        key: getCoverPickerRowKey(user),
      })),
    [filteredUsers],
  );

  const handleClose = () => {
    if (onClose) {
      onClose();
      return;
    }

    if (shadow) {
      history.goBack();
      return;
    }
    history.push(getFullUrl(PATHS.WORK_JOURNEY_MANAGEMENT, url));
  };

  const handleReplace = (user: IWorkJourneyManagementUser) => {
    if (onReplace) {
      onReplace(user);
      return;
    }

    if (!workJourneyConfigId || !date || !user?.id) return;

    history.push(
      getFullUrl(
        getRouteToCoverAbsenceReplaceDrawer({
          workJourneyConfigId,
          date,
          absentUserId: user.id,
        }),
        url,
      ),
      {
        ...location.state,
        shadow: true,
        replacedUser: user,
      } satisfies IWorkJourneyCoverDrawerLocationState,
    );
  };

  const renderDefaultStatus = () => (
    <Tag color="gold" className="gx-mb-0">
      <IntlMessages id="workJourney.management.cover.absence.status" />
    </Tag>
  );

  const columns: ResponsiveTableColumns<IWorkJourneyManagementUser> = [
    {
      title: intl.formatMessage({ id: 'workJourney.management.cover.columns.user' }),
      key: 'user',
      render: user => (
        <div className={styles.userCell}>
          <MemberAvatar size={32} user={user} />
          <div className={styles.userLabel}>
            <span>{formatCoverUserDisplayName(user)}</span>
            {user.journeyName ? (
              <span className={styles.journeyLabel}>{user.journeyName}</span>
            ) : null}
          </div>
        </div>
      ),
    },
    {
      title: intl.formatMessage({ id: 'workJourney.management.cover.columns.idCard' }),
      key: 'idCard',
      render: user => formatAttendanceIdentifierDisplay(organizationCountry, user) || '—',
    },
    {
      title: intl.formatMessage({ id: 'workJourney.management.cover.columns.status' }),
      key: 'status',
      render: user => renderStatus?.(user) ?? renderDefaultStatus(),
    },
    {
      title: '',
      key: 'action',
      align: 'right',
      render: user => (
        <ActionButton.Container
          buttons={[
            <ActionButton
              label={
                actionLabel ??
                intl.formatMessage({ id: 'workJourney.management.cover.absence.replace' })
              }
              onClick={() => handleReplace(user)}
              type="primary"
              color="primary"
              size="medium"
              allowedRoles={ALLOWED_ROLES.ORGANIZATIONS.WORK_JOURNEY.UPDATE}
              disabledScaling
            />,
          ]}
        />
      ),
    },
  ];

  return (
    <Drawer
      open={isOpen}
      width={width}
      title={null}
      closable={false}
      footer={null}
      destroyOnClose
      onClose={handleClose}
    >
      <BoxContainer>
        <BoxContainer fullContent fixed bottomBorder>
          <FilterContainer goBack={handleClose} title={<Title.Header value={title} />} />
        </BoxContainer>

        <BoxContainer fullContent>
          {showConflict && (
            <>
              <Title.SubTitle
                className="gx-mb-3"
                value={<IntlMessages id="workJourney.management.cover.conflict" />}
              />

              <WorkJourneyCoverConflictAlert
                title={<IntlMessages id="workJourney.management.cover.absence.alertTitle" />}
                description={intl.formatMessage(
                  { id: 'workJourney.management.cover.absence.alertDescription' },
                  {
                    current: currentMembers ?? '—',
                    required: requiredMembers ?? '—',
                  },
                )}
              />
            </>
          )}

          <Title.SubTitle
            className="gx-mt-4 gx-mb-3"
            value={
              sectionTitle ?? (
                <IntlMessages id="workJourney.management.cover.absence.replaceSection" />
              )
            }
          />

          <div className={styles.filtersRow}>
            <Input.Search
              className={`${styles.searchInput} gx-mb-0`}
              allowClear
              value={search}
              placeholder={intl.formatMessage({ id: searchPlaceholderId })}
              onChange={event => setSearch(event.target.value)}
            />
          </div>

          {isHydratingProfiles ? (
            <div className="gx-d-flex gx-justify-content-center gx-align-items-center gx-h-100">
              <Spin />
            </div>
          ) : filteredUsers.length === 0 ? (
            <Empty
              className="gx-my-4"
              description={intl.formatMessage({ id: 'general.empty.state' })}
            />
          ) : (
            <ResponsiveTable dataSource={tableRows} columns={columns} />
          )}
        </BoxContainer>
      </BoxContainer>
    </Drawer>
  );
};
